// PRICE COMPARISON for one quote line (M2, owner B4/B5, D197).
//
// Every OEM offer for the line's exact part (OEM quote lines of any OEM and the
// line's active confirmations), from the read-only oem_offers route. The
// RESULTING SELL PRICE of each offer comes from the EXISTING stateless
// price_lines route -- the engine prices the line as it would with that cost.
// ⚠️ NO ARITHMETIC HERE and nothing is saved by comparing. The engine prices AT
// the quote's GP %, so every offer shows that GP and what differs is the sell
// price: "best" = "Lowest price to customer" (at the quote GP %, lowest cost). An EXPIRED offer is shown
// and priced but never marked lowest or fastest. An offer in another
// currency than the quote's costs is not priced (the server says why).
//
// Choosing: the same OEM's quote line goes through the EXISTING "Use OEM quote
// price" path; a confirmation is never applied (it is what the order form
// orders from); another OEM's quote line can only be ASKED of that OEM (a
// price request). A choice that is not the best asks for an optional reason;
// the server records ONE audit row.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  api, materialToBackendLine, oemQuoteMatches, requestOtherOem, selectOemOffer,
  type OemOffer, type OemOffersLine, type OemQuoteMatch,
} from "@/services/api";
import type { Material } from "@/types";
import { formatDate, formatRupees } from "@/lib/format";
import { money } from "@/components/OemConfirmationsPanel";
import { colors, semanticColors, radius, spacing, heights, typography, layout, shadows, overlayMaxWidth } from "@/styles/tokens";

type Priced = { unitSp: number; extendedSp: number; gp: number } | { error: string } | null;
type SortKey = "sell" | "lead" | "validity";

const TH: React.CSSProperties = { textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.xs,
  color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`, whiteSpace: "nowrap" };
const TD: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm,
  borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top" };
const BTN: React.CSSProperties = { height: heights.input, padding: `0 ${spacing[10]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, cursor: "pointer", whiteSpace: "nowrap" };
const PRIMARY: React.CSSProperties = { ...BTN, border: "none", backgroundColor: colors.indigo600, color: semanticColors.onAccent };
const TAG: React.CSSProperties = { display: "inline-flex", height: heights.pill, alignItems: "center", padding: `0 ${spacing[6]}px`,
  borderRadius: radius.base, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
  backgroundColor: colors.green50, color: colors.green600, border: `1px solid ${colors.green100}`, marginRight: spacing[4] };

/** Lead time in days for SORTING only ("14 days", "3 weeks"); null when not stated. */
export function leadDays(text: string | null): number | null {
  const m = /(\d+(?:\.\d+)?)\s*(day|week|wk|month)?/i.exec(text ?? "");
  if (!m) return null;
  const n = Number(m[1]);
  const unit = (m[2] ?? "day").toLowerCase();
  return unit.startsWith("w") ? n * 7 : unit.startsWith("m") ? n * 30 : n;
}

export function offerKey(o: OemOffer) { return `${o.kind}-${o.id}`; }

export function OemCompareDialog({
  quoteId, line, row, mode, supplierCurrency, gpPct, variancePct, canAct, onUseOemQuote, onChanged, onClose,
}: {
  quoteId: number; line: OemOffersLine; row: Material; mode: "domestic" | "import"; supplierCurrency?: string | null;
  gpPct: number; variancePct: number; canAct: boolean;
  onUseOemQuote: (m: OemQuoteMatch) => void; onChanged: () => void; onClose: () => void;
}) {
  const [priced, setPriced] = useState<Record<string, Priced>>({});
  const [sort, setSort] = useState<SortKey>("sell");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  // The resulting sell price of each comparable offer: the EXISTING price_lines route.
  useEffect(() => {
    const comparable = line.offers.filter((o) => o.comparable);
    if (!comparable.length) return;
    let alive = true;
    void (async () => {
      const lines = comparable.map((o) => materialToBackendLine({ ...row, netPrice: o.price }, mode, supplierCurrency ?? undefined));
      const res = await api.priceLinesFromBackend(lines, variancePct, gpPct, mode, supplierCurrency ?? undefined);
      if (!alive) return;
      const out: Record<string, Priced> = {};
      comparable.forEach((o, i) => {
        const r = res.ok && res.lines ? res.lines[i] : undefined;
        out[offerKey(o)] = r ? { unitSp: r.unit_sp, extendedSp: r.extended_sp, gp: r.gp_pct_snapshot }
          : { error: res.error || "Not priced." };
      });
      setPriced(out);
    })();
    return () => { alive = false; };
  }, [line, row, mode, supplierCurrency, gpPct, variancePct]);

  const sell = (o: OemOffer) => { const p = priced[offerKey(o)]; return p && "unitSp" in p ? p.unitSp : null; };
  const best = useMemo(() => {
    const ok = line.offers.filter((o) => !o.expired && sell(o) != null);
    return ok.length ? ok.reduce((a, b) => ((sell(b) as number) < (sell(a) as number) ? b : a)) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line, priced]);
  const fastest = useMemo(() => {
    const ok = line.offers.filter((o) => !o.expired && leadDays(o.lead_time) != null);
    return ok.length ? ok.reduce((a, b) => ((leadDays(b.lead_time) as number) < (leadDays(a.lead_time) as number) ? b : a)) : null;
  }, [line]);
  const sorted = useMemo(() => [...line.offers].sort((a, b) => {
    const nil = (x: number | null) => (x == null ? Number.POSITIVE_INFINITY : x);
    if (sort === "lead") return nil(leadDays(a.lead_time)) - nil(leadDays(b.lead_time));
    if (sort === "validity") return (b.valid_until ?? "").localeCompare(a.valid_until ?? "");
    return nil(sell(a)) - nil(sell(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [line, sort, priced]);

  const reasonFor = (o: OemOffer): string | null | undefined => {
    if (best && offerKey(best) === offerKey(o)) return undefined;
    const r = window.prompt("This is not the offer with the lowest price to customer. Why this one? (optional)");
    return r === null ? null : r;
  };
  const record = async (o: OemOffer, reason?: string) => selectOemOffer({
    quoteId, lineId: line.line_id, kind: o.kind, id: o.id, best: !!best && offerKey(best) === offerKey(o), reason });

  const use = async (o: OemOffer) => {
    const reason = reasonFor(o);
    if (reason === null) return;
    setBusy(true); setError(""); setMsg("");
    if (o.kind === "oem_quote") {
      const m = await oemQuoteMatches({ oem: row.oem ?? line.oem, partNumber: row.partNo, qty: row.qty, quoteId });
      const match = m.ok ? (m.matches ?? []).find((x) => x.id === o.id) : undefined;
      if (!match) { setBusy(false); setError(m.error || "That OEM quote line is not offered for this line any more."); return; }
      onUseOemQuote(match);
    }
    const r = await record(o, reason || undefined);
    setBusy(false);
    if (!r.ok) { setError(r.error || "The choice was not recorded."); return; }
    setMsg(o.kind === "oem_quote" ? `${o.oem}'s price is now this line's cost (save the draft to keep it).`
      : `${o.oem}'s confirmation is the one to order from. Nothing on the quote changed.`);
    onChanged();
  };
  const ask = async (o: OemOffer) => {
    if (!o.oem_link) return;
    setBusy(true); setError(""); setMsg("");
    const r = await requestOtherOem(quoteId, o.oem_link.id, [line.line_id]);
    setBusy(false);
    if (!r.ok) { setError(r.error || "No request was made."); return; }
    setMsg(`A price request to ${o.oem_link.name} was recorded under step 2. Nothing is emailed: contact them yourself.`);
    onChanged();
  };

  return createPortal(
    <div data-testid="oem-compare" role="dialog" aria-modal="true" aria-label={`Compare offers for ${line.part_number}`}
      style={{ position: "fixed", inset: 0, backgroundColor: semanticColors.overlay, zIndex: 1000, overflowY: "auto",
        display: "flex", justifyContent: "center", alignItems: "flex-start", padding: spacing[16] }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: layout.compareDialogWidth, maxWidth: overlayMaxWidth,
        backgroundColor: semanticColors.surface, borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[16],
        display: "grid", gap: spacing[10] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap" }}>
          <strong style={{ flex: 1, fontSize: typography.fontSize.md }}>
            Line {line.line_no}, {line.part_number}: {line.offers.length} offer{line.offers.length === 1 ? "" : "s"} from {line.oem_count} OEM{line.oem_count === 1 ? "" : "s"}
          </strong>
          <label style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>Sort by{" "}
            <select data-testid="oem-compare-sort" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
              style={{ height: heights.input, borderRadius: radius.base, border: `1px solid ${semanticColors.border}` }}>
              <option value="sell">Resulting sell price</option><option value="lead">Lead time</option><option value="validity">Valid longest</option>
            </select>
          </label>
          <button data-testid="oem-compare-close" style={BTN} onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
          The sell price each offer would give comes from the pricing engine at this quote's GP %{" "}
          ({gpPct}%). Comparing saves nothing. "Use this offer" changes the line only through "Use OEM quote price".
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["OEM", "Offer", "Price", "Covers", "Lead time", "Valid until", "Resulting sell price", "GP", ""].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>{sorted.map((o) => {
              const p = priced[offerKey(o)];
              return (
                <tr key={offerKey(o)} data-testid={`oem-offer-${offerKey(o)}`}>
                  <td style={TD}>
                    {best && offerKey(best) === offerKey(o) && <span data-testid="oem-offer-best" style={TAG}>Lowest price to customer</span>}
                    {fastest && offerKey(fastest) === offerKey(o) && <span data-testid="oem-offer-fastest" style={TAG}>Fastest</span>}
                    <div style={{ fontWeight: typography.fontWeight.medium }}>{o.oem}</div>
                    {!o.oem_link && <div style={{ fontSize: typography.fontSize.xs, color: colors.amber800 }}>Not linked to an OEM</div>}
                  </td>
                  <td style={TD}>{o.kind === "oem_quote" ? `OEM quote ${o.reference ?? ""}` : `Confirmation${o.reference ? ` ${o.reference}` : ""}`}
                    {o.in_use && <div style={{ fontSize: typography.fontSize.xs, color: colors.green600 }}>In use on this line</div>}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>{money(o.price, o.currency)}</td>
                  <td style={TD}>{o.qty == null ? "Not stated" : `${o.qty} of ${line.qty}`}</td>
                  <td style={TD}>{o.lead_time ?? "Not stated"}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap", color: o.expired ? colors.red600 : undefined }}>
                    {o.valid_until ? formatDate(o.valid_until) : "Not stated"}{o.expired ? " (expired)" : ""}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }} data-testid={`oem-offer-sell-${offerKey(o)}`}>
                    {!o.comparable ? <span style={{ color: colors.amber800 }}>Not compared</span>
                      : p == null ? "…" : "error" in p ? <span style={{ color: colors.red600 }}>{p.error}</span> : formatRupees(p.unitSp)}</td>
                  <td style={TD}>{p && "gp" in p ? `${p.gp}%` : "—"}</td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    {canAct && o.kind === "oem_quote" && o.same_oem && !o.in_use && (
                      <button data-testid={`oem-offer-use-${offerKey(o)}`} style={PRIMARY} disabled={busy} onClick={() => { void use(o); }}>Use this offer</button>)}
                    {canAct && o.kind === "confirmation" && (
                      <button data-testid={`oem-offer-use-${offerKey(o)}`} style={BTN} disabled={busy} onClick={() => { void use(o); }}>Choose this offer</button>)}
                    {canAct && o.kind === "oem_quote" && !o.same_oem && (o.oem_link
                      ? <button data-testid={`oem-offer-ask-${offerKey(o)}`} style={BTN} disabled={busy} onClick={() => { void ask(o); }}>Ask {o.oem_link.name} to confirm</button>
                      : <span style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>An Admin links this name on the OEMs page first</span>)}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        {line.offers.some((o) => o.warnings.length) && (
          <ul data-testid="oem-compare-warnings" style={{ margin: 0, paddingLeft: spacing[16], fontSize: typography.fontSize.xs, color: colors.amber800 }}>
            {line.offers.flatMap((o) => o.warnings.map((w) => <li key={`${offerKey(o)}-${w}`}>{o.oem}: {w}</li>))}
          </ul>
        )}
        {msg && <div role="status" data-testid="oem-compare-msg" style={{ fontSize: typography.fontSize.sm, color: colors.green600 }}>{msg}</div>}
        {error && <div role="alert" data-testid="oem-compare-error" style={{ fontSize: typography.fontSize.sm, color: colors.red600 }}>{error}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Step 2's list: per priced line, how many offers from how many OEMs, and Compare. */
export function OemOffersSummary({ lines, onCompare }: { lines: OemOffersLine[]; onCompare: (lineId: number) => void }) {
  const withOffers = lines.filter((l) => l.offers.length > 0);
  return (
    <section data-testid="oem-offers" style={{ marginTop: spacing[12], border: `1px solid ${semanticColors.border}`,
      borderRadius: radius.base, backgroundColor: semanticColors.surface }}>
      <div style={{ display: "flex", gap: spacing[10], flexWrap: "wrap", alignItems: "center",
        padding: `${spacing[10]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}` }}>
        <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold }}>OEM offers</span>
        <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          Every OEM quote and confirmation for each line's exact part, side by side. Comparing saves nothing.</span>
      </div>
      {withOffers.length === 0 ? (
        <div data-testid="oem-offers-none" style={{ padding: spacing[12], fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>
          No OEM offer on file for these parts yet. Record OEM quotes on the OEM Quotes page, or ask an OEM below.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          {withOffers.map((l) => (
            <tr key={l.line_id} data-testid={`oem-offers-line-${l.line_no}`}>
              <td style={TD}>{l.line_no}</td><td style={{ ...TD, fontWeight: typography.fontWeight.medium }}>{l.part_number}</td>
              <td style={TD}>{l.offers.length} offer{l.offers.length === 1 ? "" : "s"} from {l.oem_count} OEM{l.oem_count === 1 ? "" : "s"}</td>
              <td style={TD}><button data-testid={`oem-offers-compare-${l.line_no}`} style={BTN} onClick={() => onCompare(l.line_id)}>Compare</button></td>
            </tr>))}
        </tbody></table>
      )}
    </section>
  );
}
